/**
 * MobileChattr.jsx — Production v4 (Auto-Broaden Matching)
 *
 * ── CHANGE LOG from v3 ──────────────────────────────────────────────────────
 *
 * NEW · AUTO-BROADEN MATCHING
 *   Problem: Users searching for a niche interest (e.g. "Parenting") may never
 *            find a match because no one else is queued for that topic.
 *   Solution: After 30 seconds of interest-specific searching, the queue
 *             automatically broadens to match with ANY available user.
 *
 *   Implementation:
 *     - Hook level: new `broadenQueue(nickname, newInterest)` function sends
 *       cancel_queue + join_queue atomically in the same tick over the same
 *       WebSocket. A `isBroadeningRef` flag suppresses the intermediate
 *       "queue_cancelled" and any racing "match_timeout" from the server,
 *       so queueStatus never transitions to "idle" — no view flash.
 *     - Component level: a `broadened` state drives UI changes in QueuedView
 *       (icon, title, subtitle, timer text). A `useEffect` at elapsed === 30
 *       calls `broadenQueue` and resets the elapsed timer.
 *     - nicknameRef keeps the nickname accessible to the broaden effect
 *       without adding `nickname` to the dependency array (which would
 *       re-create the effect on every keystroke).
 *
 *   Edge cases handled:
 *     - Server "queue_cancelled" during broaden → suppressed by hook
 *     - Server "match_timeout" racing with broaden → suppressed by hook
 *     - Match arrives during broaden → accepted (flag consumed)
 *     - User cancels during broadened search → works normally
 *     - WebSocket disconnects during broaden → reconnect logic unchanged
 *
 * All v3 bug fixes preserved (FIX 1–3).
 * All v2 logic preserved: useChattrSocket wiring, Promise modal, joinQueue/
 * cancelQueue, queueStatus sync, bad-word filter, typing debounce, emoji
 * reactions, report modal, leave confirm, custom interest pill, starter
 * suggestions, mood-adaptive ambient mesh, haptics, safe-area insets.
 */

import React, {
  useState, useEffect, useRef, useCallback, useMemo,
} from "react";
import { auth } from "../../firebase";
import { useChattrSocket } from "../../hooks/useChattrSocket";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

/** How many seconds to search by specific interest before broadening */
const BROADEN_AFTER_SECONDS = 25;

const INTERESTS = [
  { value: "Anxiety",         emoji: "🌀" },
  { value: "Depression",      emoji: "🌧️" },
  { value: "Stress",          emoji: "💨" },
  { value: "Loneliness",      emoji: "🌙" },
  { value: "Relationships",   emoji: "💞" },
  { value: "Self-Esteem",     emoji: "🌱" },
  { value: "Burnout",         emoji: "🕯️" },
  { value: "Mindfulness",     emoji: "🍃" },
  { value: "Motivation",      emoji: "⚡" },
  { value: "Grief",           emoji: "🕊️" },
  { value: "Career Pressure", emoji: "📚" },
  { value: "Breakup",         emoji: "💔" },
  { value: "Parenting",       emoji: "🌻" },
  { value: "Bullying",        emoji: "🛡️" },
  { value: "Bollywood",       emoji: "🎬" },
  { value: "Cricket",         emoji: "🏏" },
  { value: "Music",           emoji: "🎵" },
  { value: "Gaming",          emoji: "🎮" },
  { value: "Football",        emoji: "⚽" },
  { value: "Politics",        emoji: "🗳️" },
];

const BAD_WORDS = [
  "fuck","shit","bastard","bitch","asshole","dick","pussy","cunt",
  "motherfucker","fucker","chutiya","madarchod","behenchod","bhenchod",
  "gandu","harami","randi","saala","mc","bc","lund","gaand","bhosdike",
  "nigger","faggot","retard","whore","slut","rape",
];

function containsBadWord(text) {
  const lower = text.toLowerCase().replace(/[^a-z0-9\u0900-\u097F ]/g, "");
  return BAD_WORDS.some(w => lower.includes(w));
}

const PROMISES = [
  "I promise to honor your story and always remain respectful.",
  "I pledge to protect this safe space and always remain kind.",
  "I promise to choose empathy over judgment and always remain patient.",
  "I pledge to be the light in someone's day and always remain gentle.",
  "I promise to listen with an open heart and always remain present.",
  "I pledge to lift others up and always remain supportive.",
  "I promise to respect your privacy and always remain trustworthy.",
  "I pledge to keep this conversation safe and always remain mindful.",
  "I promise to value our shared time and always remain sincere.",
  "I pledge to offer warmth to a stranger and always remain compassionate.",
];

const PROMISE_COOLDOWN_MS = 60 * 60 * 1000;

function shouldShowPromise() {
  const last = localStorage.getItem("chattr_last_promise");
  if (!last) return true;
  return Date.now() - parseInt(last, 10) > PROMISE_COOLDOWN_MS;
}
function markPromiseSeen() {
  localStorage.setItem("chattr_last_promise", Date.now().toString());
}
function getRandomPromise() {
  return PROMISES[Math.floor(Math.random() * PROMISES.length)];
}

const REACTION_EMOJIS = ["❤️", "😂", "😮", "😢", "👍", "🙏"];

const REPORT_REASONS = [
  "Harassment or bullying",
  "Inappropriate sexual content",
  "Hate speech or discrimination",
  "Threatening language",
  "Spam or fake account",
  "Other",
];

// ─── MOOD SYSTEM ──────────────────────────────────────────────────────────────

const MOOD_THEMES = {
  "Anxiety":         { primary: "#2A9D8F", accent: "#21867A", secondary: "#E0F2F1", mesh: "42,157,143" },
  "Depression":      { primary: "#5C6BC0", accent: "#3F51B5", secondary: "#E8EAF6", mesh: "92,107,192" },
  "Stress":          { primary: "#42A5F5", accent: "#1E88E5", secondary: "#E3F2FD", mesh: "66,165,245" },
  "Loneliness":      { primary: "#FF8A65", accent: "#F4511E", secondary: "#FFF3E0", mesh: "255,138,101" },
  "Relationships":   { primary: "#EC407A", accent: "#D81B60", secondary: "#FCE4EC", mesh: "236,64,122" },
  "Self-Esteem":     { primary: "#FFA726", accent: "#F57C00", secondary: "#FFF8E1", mesh: "255,167,38" },
  "Burnout":         { primary: "#78909C", accent: "#546E7A", secondary: "#ECEFF1", mesh: "120,144,156" },
  "Mindfulness":     { primary: "#66BB6A", accent: "#43A047", secondary: "#E8F5E9", mesh: "102,187,106" },
  "Motivation":      { primary: "#FFCA28", accent: "#F57F17", secondary: "#FFFDE7", mesh: "255,202,40" },
  "Grief":           { primary: "#8D6E63", accent: "#6D4C41", secondary: "#EFEBE9", mesh: "141,110,99" },
  "Career Pressure": { primary: "#5C6BC0", accent: "#3949AB", secondary: "#E8EAF6", mesh: "92,107,192" },
  "Breakup":         { primary: "#AB47BC", accent: "#8E24AA", secondary: "#F3E5F5", mesh: "171,71,188" },
  "Parenting":       { primary: "#FFB300", accent: "#F57F17", secondary: "#FFF8E1", mesh: "255,179,0" },
  "Bullying":        { primary: "#26A69A", accent: "#00796B", secondary: "#E0F2F1", mesh: "38,166,154" },
  "Bollywood":       { primary: "#EF5350", accent: "#C62828", secondary: "#FFEBEE", mesh: "239,83,80" },
  "Cricket":         { primary: "#66BB6A", accent: "#2E7D32", secondary: "#E8F5E9", mesh: "102,187,106" },
  "Music":           { primary: "#AB47BC", accent: "#6A1B9A", secondary: "#F3E5F5", mesh: "171,71,188" },
  "Gaming":          { primary: "#42A5F5", accent: "#1565C0", secondary: "#E3F2FD", mesh: "66,165,245" },
  "Football":        { primary: "#26C6DA", accent: "#00838F", secondary: "#E0F7FA", mesh: "38,198,218" },
  "Politics":        { primary: "#78909C", accent: "#37474F", secondary: "#ECEFF1", mesh: "120,144,156" },
  "Default":         { primary: "#5A6993", accent: "#323B52", secondary: "#E8EAF6", mesh: "90,105,147" },
};

function getMood(topic) {
  return MOOD_THEMES[topic] || MOOD_THEMES["Default"];
}

// ─── STARTER MESSAGES ─────────────────────────────────────────────────────────

const STARTERS = {
  "Anxiety":    ["I've been feeling really anxious lately.", "My mind won't stop racing today.", "Trying to ground myself right now."],
  "Depression": ["Some days are just really hard.", "Feeling pretty low today.", "Just looking for someone to listen."],
  "Stress":     ["Work has been incredibly overwhelming.", "I just need to vent for a minute.", "There's too much on my plate right now."],
  "Loneliness": ["It's been feeling pretty lonely lately.", "Just wanted to connect with someone.", "Saying hi so I don't feel so alone today."],
  "Relationships": ["Navigating relationships is confusing.", "Having trouble with a close connection.", "Why is communication so hard sometimes?"],
  "Self-Esteem": ["Struggling with my confidence today.", "How do you build yourself up on bad days?", "Trying to be kinder to myself."],
  "Burnout":    ["I feel completely drained.", "Running on empty over here.", "How do you recover from burnout?"],
  "Mindfulness":["Trying to stay present today.", "Just taking a moment to breathe.", "Reflecting on my day right now."],
  "Motivation": ["Having trouble finding my drive.", "How do you stay motivated?", "Feeling stuck in a rut."],
  "Grief":      ["Dealing with a lot of loss right now.", "Grief comes in waves, today is a hard one.", "Just missing someone a lot today."],
  "Career Pressure": ["The pressure at work is getting to me.", "Feeling stuck in my career path.", "Imposter syndrome is hitting hard today."],
  "Breakup":    ["Going through a tough breakup.", "It hurts more than I thought.", "Trying to figure out how to move on."],
  "Default":    ["Hi, how are you doing today?", "Just looking for a friendly chat.", "What's on your mind today?"],
};

function getStarters(topic) {
  const list = STARTERS[topic] || STARTERS["Default"];
  return [...list].sort(() => 0.5 - Math.random()).slice(0, 3);
}

// ─── HAPTIC ───────────────────────────────────────────────────────────────────

function haptic(type = "light") {
  if (typeof window !== "undefined" && window.navigator?.vibrate) {
    window.navigator.vibrate(type === "heavy" ? [40, 30, 40] : 20);
  }
}

// ─── STYLES ───────────────────────────────────────────────────────────────────

const BASE_CSS = `
  /* ── ROOT ── */
  .mc2-root {
    display: flex;
    flex-direction: column;
    background: var(--bg, #F9F4F0);
    position: relative;
    overflow: hidden;
    font-family: var(--font, 'Satoshi', 'Plus Jakarta Sans', sans-serif);
    -webkit-font-smoothing: antialiased;
    -webkit-tap-highlight-color: transparent;
  }

  .mc2-root--chat {
    position: fixed;
    inset: 0;
    z-index: 200;
    height: 100vh;
    height: 100dvh;
    overflow: hidden;
  }

  /* Ambient mesh */
  .mc2-mesh {
    position: absolute;
    inset: -50%;
    width: 200%;
    height: 200%;
    pointer-events: none;
    z-index: 0;
    transition: background 0.8s ease;
    animation: mc2MeshDrift 20s ease-in-out infinite alternate;
  }
  @keyframes mc2MeshDrift {
    0%   { transform: translate(0,0) rotate(0deg); }
    100% { transform: translate(-4%,4%) rotate(4deg); }
  }

  /* ── HOME ── */
  .mc2-home {
    position: relative;
    z-index: 1;
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: max(env(safe-area-inset-top, 20px), 20px) 22px calc(env(safe-area-inset-bottom, 0px) + 32px);
  }

  .mc2-home-title {
    font-family: var(--font-display, 'Fraunces', Georgia, serif);
    font-size: 3rem;
    font-weight: 800;
    line-height: 1;
    margin: 0 0 6px;
    letter-spacing: -0.03em;
    color: var(--mc2-primary, #5A6993);
    transition: color 0.5s ease;
  }
  .mc2-home-sub {
    font-size: 0.95rem;
    color: var(--muted, #9C8474);
    line-height: 1.55;
    margin-bottom: 32px;
    font-weight: 500;
  }
  .mc2-field { margin-bottom: 26px; }
  .mc2-label {
    display: block;
    font-size: 0.68rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--muted, #9C8474);
    margin-bottom: 10px;
    margin-left: 4px;
  }
  .mc2-nick-input {
    width: 100%;
    padding: 16px 20px;
    font-family: var(--font, sans-serif);
    font-size: 16px;
    font-weight: 600;
    background: var(--card, #fff);
    color: var(--ink, #2D364D);
    border: 1.5px solid var(--sand-light, #EBE7DF);
    border-radius: 18px;
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.25s, box-shadow 0.25s, transform 0.25s;
    -webkit-tap-highlight-color: transparent;
  }
  .mc2-nick-input:focus {
    border-color: var(--mc2-primary, #5A6993);
    box-shadow: 0 0 0 4px rgba(var(--mc2-mesh, 90,105,147), 0.14);
    transform: translateY(-1px);
  }
  .mc2-nick-input::placeholder { color: var(--sand, #D6D2C4); font-weight: 400; }

  /* Chip grid */
  .mc2-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 9px;
  }
  .mc2-chip {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 11px 16px;
    border-radius: 99px;
    border: 1.5px solid var(--sand-light, #EBE7DF);
    background: var(--card, #fff);
    color: var(--ink, #2D364D);
    font-family: var(--font, sans-serif);
    font-size: 0.88rem;
    font-weight: 600;
    cursor: pointer;
    outline: none;
    transition: all 0.22s cubic-bezier(0.25,1,0.5,1);
    box-shadow: 0 2px 8px rgba(0,0,0,0.03);
    -webkit-tap-highlight-color: transparent;
  }
  .mc2-chip:active { transform: scale(0.93); }
  .mc2-chip.active {
    background: var(--mc2-primary, #5A6993);
    border-color: var(--mc2-primary, #5A6993);
    color: #fff;
    box-shadow: 0 6px 18px rgba(var(--mc2-mesh, 90,105,147), 0.35);
    transform: translateY(-2px);
  }
  .mc2-custom-input {
    padding: 11px 16px;
    border-radius: 99px;
    border: 1.5px solid var(--mc2-primary, #5A6993);
    background: var(--card, #fff);
    color: var(--ink, #2D364D);
    font-family: var(--font, sans-serif);
    font-size: 16px;
    font-weight: 600;
    outline: none;
    width: 140px;
    box-shadow: 0 0 0 4px rgba(var(--mc2-mesh, 90,105,147), 0.12);
    transition: width 0.35s cubic-bezier(0.25,1,0.5,1);
    -webkit-tap-highlight-color: transparent;
  }
  .mc2-custom-input:focus { width: 210px; }

  /* CTA button */
  .mc2-cta {
    width: 100%;
    padding: 19px;
    margin-top: 8px;
    background: var(--mc2-primary, #5A6993);
    color: #fff;
    border: none;
    border-radius: 22px;
    font-family: var(--font, sans-serif);
    font-size: 1.05rem;
    font-weight: 700;
    cursor: pointer;
    outline: none;
    overflow: hidden;
    position: relative;
    box-shadow: 0 10px 28px rgba(var(--mc2-mesh, 90,105,147), 0.32);
    transition: all 0.25s cubic-bezier(0.25,1,0.5,1);
    -webkit-tap-highlight-color: transparent;
  }
  .mc2-cta::after {
    content: '';
    position: absolute;
    top: 0; left: -100%; width: 50%; height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.22), transparent);
    animation: mc2Shine 3s infinite;
  }
  @keyframes mc2Shine { 0%{left:-100%} 20%{left:200%} 100%{left:200%} }
  .mc2-cta:active:not(:disabled) { transform: scale(0.97); }
  .mc2-cta:disabled {
    opacity: 0.45;
    box-shadow: none;
    background: var(--sand-light, #EBE7DF);
    color: var(--muted, #9C8474);
    cursor: not-allowed;
  }
  .mc2-cta:disabled::after { display: none; }

  /* Status bar */
  .mc2-status-bar {
    display: flex; align-items: center; gap: 8px;
    padding: 10px 16px; margin-bottom: 12px;
    background: var(--card, #fff); border-radius: 14px;
    border: 1px solid var(--sand-light, #EBE7DF);
    font-size: 0.8rem; font-weight: 600; color: var(--muted, #9C8474);
  }
  .mc2-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
  .mc2-dot--connecting { background: #FF9500; animation: mc2Pulse 1.4s infinite; }
  .mc2-dot--connected  { background: #34C759; }
  .mc2-dot--disconnected { background: #FF3B30; }
  @keyframes mc2Pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }

  /* Error banner */
  .mc2-error {
    display: flex; align-items: center; justify-content: space-between;
    padding: 12px 16px; margin-bottom: 14px;
    background: #FFEBEB; color: #C0392B;
    border-radius: 14px; border: 1px solid rgba(192,57,43,0.2);
    font-size: 0.82rem; font-weight: 600;
  }
  .mc2-error button {
    background: none; border: none; color: #C0392B;
    font-size: 1.1rem; cursor: pointer; padding: 0 4px; outline: none;
  }

  /* Share row */
  .mc2-share {
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 18px; margin-top: 16px;
    background: var(--card, #fff); border-radius: 18px;
    border: 1px solid var(--sand-light, #EBE7DF);
  }
  .mc2-share-text { font-size: 0.78rem; color: var(--muted, #9C8474); font-weight: 500; flex: 1; padding-right: 12px; }
  .mc2-share-btn {
    padding: 9px 16px; background: #25D366; color: #fff; border: none;
    border-radius: 12px; font-family: var(--font, sans-serif); font-size: 0.8rem;
    font-weight: 700; cursor: pointer; outline: none;
    white-space: nowrap; -webkit-tap-highlight-color: transparent;
  }

  /* ── QUEUED ── */
  .mc2-queued {
    position: relative; z-index: 1;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    padding: 40px 28px;
    min-height: 80vh;
    min-height: 80svh;
    gap: 0;
  }
  .mc2-radar {
    position: relative;
    width: 160px; height: 160px;
    display: flex; align-items: center; justify-content: center;
    margin-bottom: 36px;
  }
  .mc2-radar-ring {
    position: absolute; inset: 0; border-radius: 50%;
    border: 2px solid var(--mc2-primary, #5A6993);
    opacity: 0;
    animation: mc2RadarExpand 2.4s cubic-bezier(0.21,0.93,0.38,1) infinite;
  }
  .mc2-radar-ring:nth-child(2) { animation-delay: 0.8s; }
  .mc2-radar-ring:nth-child(3) { animation-delay: 1.6s; }
  @keyframes mc2RadarExpand {
    0%   { transform: scale(0.25); opacity: 0.9; }
    100% { transform: scale(1.6);  opacity: 0; }
  }
  .mc2-radar-icon {
    font-size: 2.8rem; position: relative; z-index: 2;
    animation: mc2Float 3s ease-in-out infinite;
  }
  @keyframes mc2Float {
    0%,100% { transform: translateY(0); }
    50%      { transform: translateY(-10px); }
  }
  .mc2-queued-title {
    font-family: var(--font-display, 'Fraunces', serif); font-size: 1.5rem; font-weight: 800;
    color: var(--ink, #2D364D); margin: 0 0 6px; text-align: center;
  }
  .mc2-queued-sub {
    font-size: 0.9rem; color: var(--muted, #9C8474); text-align: center; margin: 0 0 20px;
    line-height: 1.5;
  }
  .mc2-timer-wrap { width: 100%; max-width: 260px; margin-bottom: 32px; }
  .mc2-timer-track {
    height: 4px; background: var(--sand-light, #EBE7DF); border-radius: 2px; overflow: hidden; margin-bottom: 6px;
  }
  .mc2-timer-fill {
    height: 100%; border-radius: 2px;
    background: var(--mc2-primary, #5A6993);
    transition: width 1s linear;
  }
  .mc2-timer-text {
    font-size: 0.72rem; color: var(--muted, #9C8474); font-weight: 600; text-align: center; display: block;
  }

  /* Broadened badge — shown when search expands to all topics */
  .mc2-broadened-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    margin-bottom: 16px;
    background: rgba(var(--mc2-mesh, 90,105,147), 0.1);
    color: var(--mc2-primary, #5A6993);
    border-radius: 99px;
    font-size: 0.78rem;
    font-weight: 700;
    animation: mc2FadeIn 0.4s ease;
  }

  .mc2-cancel-btn {
    padding: 14px 32px; background: none; color: var(--muted, #9C8474);
    border: 2px solid var(--sand-light, #EBE7DF); border-radius: 99px;
    font-family: var(--font, sans-serif); font-size: 0.95rem; font-weight: 700;
    cursor: pointer; outline: none; transition: all 0.2s;
    -webkit-tap-highlight-color: transparent;
  }
  .mc2-cancel-btn:active { transform: scale(0.95); }

  /* ── CHAT ── */
  .mc2-chat {
    position: relative;
    z-index: 1;
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-rows: auto 1fr auto;
    overflow: hidden;
    background: var(--bg, #F9F4F0);
  }

  /* Frosted header — grid row 1 */
  .mc2-chat-hdr {
    position: relative;
    z-index: 20;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: calc(env(safe-area-inset-top, 14px) + 10px) 18px 14px;
    background: rgba(255,255,255,0.88);
    backdrop-filter: blur(24px);
    -webkit-backdrop-filter: blur(24px);
    border-bottom: 1px solid var(--sand-light, #EBE7DF);
    animation: mc2SlideDown 0.35s ease;
  }
  body.dark-theme .mc2-chat-hdr { background: rgba(30,34,48,0.9); }
  @keyframes mc2SlideDown { from{transform:translateY(-100%)} to{transform:translateY(0)} }

  .mc2-hdr-left { display: flex; align-items: center; gap: 12px; }
  .mc2-hdr-avatar {
    width: 40px; height: 40px; border-radius: 50%;
    background: rgba(var(--mc2-mesh, 90,105,147), 0.15);
    display: flex; align-items: center; justify-content: center;
    font-size: 1.25rem; border: 1.5px solid var(--sand-light, #EBE7DF);
    flex-shrink: 0;
  }
  .mc2-hdr-name { font-weight: 800; font-size: 1rem; color: var(--ink, #2D364D); line-height: 1.2; }
  .mc2-hdr-status {
    font-size: 0.72rem; font-weight: 600;
    color: var(--mc2-primary, #5A6993);
    display: flex; align-items: center; gap: 4px;
  }
  .mc2-online-dot {
    width: 6px; height: 6px; border-radius: 50%;
    background: #34C759;
    animation: mc2Pulse 2s infinite;
  }
  .mc2-hdr-right { display: flex; align-items: center; gap: 8px; }
  .mc2-menu-wrap { position: relative; }
  .mc2-menu-btn {
    width: 36px; height: 36px;
    background: var(--cloud, #F4F7F9); border: 1px solid var(--sand-light, #EBE7DF);
    border-radius: 50%; font-size: 1.2rem; font-weight: 700;
    color: var(--muted, #9C8474); cursor: pointer; outline: none;
    display: flex; align-items: center; justify-content: center; line-height: 1;
    -webkit-tap-highlight-color: transparent;
  }
  .mc2-dropdown {
    position: absolute; right: 0; top: calc(100% + 6px);
    background: var(--card, #fff); border: 1px solid var(--sand-light, #EBE7DF);
    border-radius: 16px; box-shadow: 0 8px 32px rgba(0,0,0,0.12);
    overflow: hidden; z-index: 50; min-width: 180px;
    animation: mc2FadeIn 0.2s ease;
  }
  @keyframes mc2FadeIn { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:translateY(0)} }
  .mc2-dd-item {
    display: block; width: 100%; padding: 14px 18px;
    background: none; border: none; text-align: left; outline: none;
    font-family: var(--font, sans-serif); font-size: 0.88rem; font-weight: 600;
    color: var(--ink, #2D364D); cursor: pointer;
    border-bottom: 1px solid var(--sand-light, #EBE7DF);
    transition: background 0.15s; -webkit-tap-highlight-color: transparent;
  }
  .mc2-dd-item:last-child { border-bottom: none; }
  .mc2-dd-item:active { background: var(--cloud, #F4F7F9); }
  .mc2-dd-item--danger { color: #FF3B30; }
  .mc2-leave-btn {
    padding: 8px 18px;
    background: rgba(255,59,48,0.1);
    color: #FF3B30; border: none; border-radius: 14px; outline: none;
    font-family: var(--font, sans-serif); font-size: 0.88rem; font-weight: 700;
    cursor: pointer; transition: transform 0.15s; -webkit-tap-highlight-color: transparent;
  }
  .mc2-leave-btn:active { transform: scale(0.92); }

  /* Messages scroll — grid row 2 */
  .mc2-msgs {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior-y: contain;
    padding: 16px 14px 20px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-height: 0;
  }

  .mc2-system-msg {
    align-self: center;
    display: flex; flex-direction: column; align-items: center; gap: 3px;
    background: rgba(var(--mc2-mesh, 90,105,147), 0.1);
    color: var(--mc2-primary, #5A6993);
    padding: 8px 16px; border-radius: 14px;
    font-size: 0.75rem; font-weight: 600;
    margin: 14px 0; text-align: center;
    transition: all 0.5s;
  }
  .mc2-system-sub { font-size: 0.68rem; color: var(--muted, #9C8474); font-weight: 500; }
  .mc2-system-msg--end { background: var(--cloud, #F4F7F9); color: var(--ink, #2D364D); }

  /* Bubble rows */
  .mc2-row {
    display: flex;
    animation: mc2PopIn 0.28s cubic-bezier(0.25,1,0.5,1) both;
  }
  @keyframes mc2PopIn {
    0%   { opacity: 0; transform: scale(0.94) translateY(8px); }
    100% { opacity: 1; transform: scale(1) translateY(0); }
  }
  .mc2-row--self  { justify-content: flex-end; }
  .mc2-row--other { justify-content: flex-start; }

  .mc2-avatar-sm {
    width: 28px; height: 28px; border-radius: 50%;
    background: rgba(var(--mc2-mesh, 90,105,147), 0.15);
    color: var(--mc2-primary, #5A6993); font-weight: 800; font-size: 0.78rem;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0; margin-right: 8px; align-self: flex-end;
  }
  .mc2-bubble-group { display: flex; flex-direction: column; max-width: 76%; }
  .mc2-bubble-sender { font-size: 0.65rem; font-weight: 700; color: var(--muted, #9C8474); margin: 0 4px 3px; }
  .mc2-bubble-with-reaction { position: relative; }
  .mc2-bubble {
    padding: 11px 15px; font-size: 0.96rem; line-height: 1.45;
    border-radius: 20px; word-wrap: break-word; position: relative;
  }
  .mc2-bubble--self {
    background: linear-gradient(135deg, var(--mc2-primary, #5A6993), var(--mc2-accent, #323B52));
    color: #fff;
    border-bottom-right-radius: 5px;
    box-shadow: 0 3px 10px rgba(var(--mc2-mesh, 90,105,147), 0.3);
  }
  .mc2-bubble--other {
    background: var(--card, #fff);
    color: var(--ink, #2D364D);
    border-bottom-left-radius: 5px;
    border: 1px solid var(--sand-light, #EBE7DF);
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  }
  body.dark-theme .mc2-bubble--other {
    background: #2C2C2E; border-color: rgba(255,255,255,0.08); color: #E5E5EA;
  }
  .mc2-bubble-time {
    font-size: 0.6rem; color: var(--muted, #9C8474); margin: 3px 4px 0; font-weight: 500;
  }
  .mc2-bubble-time--self { text-align: right; }

  /* Reaction picker */
  .mc2-reaction-picker {
    position: absolute; bottom: calc(100% + 6px); right: 0;
    display: flex; gap: 4px; padding: 6px 10px;
    background: var(--card, #fff); border: 1px solid var(--sand-light, #EBE7DF);
    border-radius: 99px; box-shadow: 0 8px 24px rgba(0,0,0,0.12);
    z-index: 30; animation: mc2FadeIn 0.18s ease; white-space: nowrap;
  }
  .mc2-reaction-picker--self { right: auto; left: 0; }
  .mc2-rxn-btn {
    background: none; border: none; font-size: 1.15rem; cursor: pointer; outline: none;
    padding: 2px 3px; border-radius: 8px; transition: transform 0.15s;
    -webkit-tap-highlight-color: transparent;
  }
  .mc2-rxn-btn:active  { transform: scale(1.4); }
  .mc2-rxn-btn--active { background: rgba(var(--mc2-mesh, 90,105,147), 0.15); }
  .mc2-reaction-display { display: flex; gap: 4px; margin: 3px 4px 0; }
  .mc2-reaction-display--self { justify-content: flex-end; }
  .mc2-rxn-emoji {
    font-size: 0.88rem; padding: 2px 7px;
    background: var(--cloud, #F4F7F9); border-radius: 99px;
    border: 1px solid var(--sand-light, #EBE7DF); cursor: pointer; font-weight: 600;
  }
  .mc2-rxn-emoji--mine   { border-color: var(--mc2-primary, #5A6993); }
  .mc2-rxn-emoji--both   { background: rgba(var(--mc2-mesh, 90,105,147), 0.12); }

  /* Typing bubble */
  .mc2-typing-bubble {
    display: flex; gap: 4px; align-items: center;
    padding: 13px 16px;
    background: var(--card, #fff); border: 1px solid var(--sand-light, #EBE7DF);
    border-radius: 20px; border-bottom-left-radius: 5px; width: fit-content;
  }
  .mc2-td {
    width: 7px; height: 7px; border-radius: 50%; background: var(--muted, #9C8474);
    animation: mc2TypBounce 1.4s ease-in-out infinite both;
  }
  .mc2-td:nth-child(2) { animation-delay: 0.15s; }
  .mc2-td:nth-child(3) { animation-delay: 0.30s; }
  @keyframes mc2TypBounce {
    0%,80%,100% { transform: scale(0); opacity: 0.4; }
    40%          { transform: scale(1); opacity: 1; }
  }

  /* Bad word warning */
  .mc2-blocked {
    flex-shrink: 0;
    margin: 0 14px 6px;
    padding: 10px 14px;
    background: #FFF3CD; color: #856404;
    border-radius: 12px; border: 1px solid #FFE083;
    font-size: 0.8rem; font-weight: 600;
    animation: mc2FadeIn 0.2s ease;
  }

  /* ── BOTTOM — grid row 3 ── */
  .mc2-bottom {
    position: relative;
    z-index: 20;
    display: flex;
    flex-direction: column;
  }

  /* Suggestions strip */
  .mc2-suggestions {
    display: flex; gap: 8px;
    overflow-x: auto; scrollbar-width: none;
    padding: 8px 14px 10px;
    -webkit-overflow-scrolling: touch;
    animation: mc2SlideUp 0.4s cubic-bezier(0.25,1,0.5,1) both;
  }
  .mc2-suggestions::-webkit-scrollbar { display: none; }
  @keyframes mc2SlideUp {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .mc2-suggestion {
    flex-shrink: 0; padding: 9px 15px; border-radius: 99px;
    background: rgba(255,255,255,0.82); backdrop-filter: blur(10px);
    border: 1px solid rgba(var(--mc2-mesh, 90,105,147), 0.2);
    color: var(--mc2-primary, #5A6993);
    font-family: var(--font, sans-serif); font-size: 0.82rem; font-weight: 600;
    cursor: pointer; outline: none; transition: all 0.2s cubic-bezier(0.25,1,0.5,1);
    box-shadow: 0 3px 12px rgba(0,0,0,0.06); white-space: nowrap;
    -webkit-tap-highlight-color: transparent;
  }
  body.dark-theme .mc2-suggestion {
    background: rgba(28,28,30,0.88); border-color: rgba(255,255,255,0.1);
  }
  .mc2-suggestion:active {
    transform: scale(0.94);
    background: rgba(var(--mc2-mesh, 90,105,147), 0.15);
  }

  /* Input bar */
  .mc2-input-bar {
    display: flex; align-items: flex-end; gap: 10px;
    padding: 12px 14px calc(12px + env(safe-area-inset-bottom, 0px));
    background: rgba(255,255,255,0.92);
    backdrop-filter: blur(24px);
    -webkit-backdrop-filter: blur(24px);
    border-top: 1px solid var(--sand-light, #EBE7DF);
  }
  body.dark-theme .mc2-input-bar { background: rgba(30,34,48,0.94); }

  .mc2-textarea {
    flex: 1;
    min-height: 44px; max-height: 120px;
    padding: 11px 14px;
    font-family: var(--font, sans-serif);
    font-size: 16px;
    line-height: 1.4;
    background: var(--cloud, #F4F7F9);
    color: var(--ink, #2D364D);
    border: 1.5px solid var(--sand-light, #EBE7DF);
    border-radius: 22px;
    outline: none; resize: none;
    overflow-y: auto;
    -webkit-appearance: none;
    transition: border-color 0.2s;
    box-sizing: border-box;
    -webkit-tap-highlight-color: transparent;
  }
  .mc2-textarea:focus { border-color: var(--mc2-primary, #5A6993); }
  .mc2-textarea::placeholder { color: var(--sand, #D6D2C4); }
  .mc2-textarea--blocked { border-color: #FFB300 !important; }
  body.dark-theme .mc2-textarea {
    background: #2C2C2E; color: #E5E5EA; border-color: rgba(255,255,255,0.1);
  }
  .mc2-send {
    width: 44px; height: 44px; flex-shrink: 0; border-radius: 50%;
    background: var(--mc2-primary, #5A6993); color: #fff; border: none; outline: none;
    display: flex; align-items: center; justify-content: center; cursor: pointer;
    box-shadow: 0 4px 14px rgba(var(--mc2-mesh, 90,105,147), 0.35);
    transition: all 0.2s cubic-bezier(0.25,1,0.5,1);
    -webkit-tap-highlight-color: transparent;
  }
  .mc2-send:active:not(:disabled) { transform: scale(0.85); }
  .mc2-send:disabled { background: var(--sand-light, #EBE7DF); box-shadow: none; color: var(--muted, #9C8474); }

  /* ── MODALS / SHEETS ── */
  .mc2-overlay {
    position: absolute; inset: 0; z-index: 100;
    background: rgba(0,0,0,0.48);
    backdrop-filter: blur(6px);
    display: flex; align-items: flex-end;
    animation: mc2FadeIn 0.25s ease;
  }
  .mc2-sheet {
    width: 100%; background: var(--card, #fff);
    border-top-left-radius: 30px; border-top-right-radius: 30px;
    padding: 10px 24px calc(32px + env(safe-area-inset-bottom, 0px));
    box-shadow: 0 -12px 48px rgba(0,0,0,0.14);
    animation: mc2SheetUp 0.38s cubic-bezier(0.25,1,0.5,1);
    display: flex; flex-direction: column; align-items: center; text-align: center;
  }
  @keyframes mc2SheetUp {
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
  }
  .mc2-sheet-handle {
    width: 40px; height: 4px; border-radius: 2px;
    background: var(--sand-light, #EBE7DF); margin: 0 auto 24px;
  }
  .mc2-sheet-icon  { font-size: 3rem; margin-bottom: 14px; }
  .mc2-sheet-title {
    font-family: var(--font-display, 'Fraunces', serif); font-size: 1.45rem; font-weight: 800;
    color: var(--ink, #2D364D); margin: 0 0 10px;
  }
  .mc2-sheet-text  { font-size: 0.9rem; color: var(--muted, #9C8474); line-height: 1.6; margin: 0 0 28px; }
  .mc2-sheet-quote {
    font-family: var(--font-display, 'Fraunces', serif); font-style: italic;
    font-size: 1.05rem; color: var(--ink, #2D364D);
    border-left: 3px solid var(--mc2-primary, #5A6993);
    padding-left: 16px; text-align: left; margin: 0 0 24px; line-height: 1.6; width: 100%;
  }
  .mc2-sheet-actions { display: flex; flex-direction: column; gap: 10px; width: 100%; }
  .mc2-sheet-primary {
    padding: 17px; border-radius: 18px; border: none; outline: none;
    background: var(--mc2-primary, #5A6993); color: #fff;
    font-family: var(--font, sans-serif); font-size: 1rem; font-weight: 700;
    cursor: pointer; transition: all 0.2s; -webkit-tap-highlight-color: transparent;
  }
  .mc2-sheet-primary:active { transform: scale(0.97); }
  .mc2-sheet-secondary {
    padding: 17px; border-radius: 18px; outline: none;
    background: var(--cloud, #F4F7F9); color: var(--ink, #2D364D);
    border: 1.5px solid var(--sand-light, #EBE7DF);
    font-family: var(--font, sans-serif); font-size: 1rem; font-weight: 700;
    cursor: pointer; transition: all 0.2s; -webkit-tap-highlight-color: transparent;
  }
  .mc2-sheet-danger {
    padding: 17px; border-radius: 18px; border: none; outline: none;
    background: rgba(255,59,48,0.1); color: #FF3B30;
    font-family: var(--font, sans-serif); font-size: 1rem; font-weight: 700;
    cursor: pointer; -webkit-tap-highlight-color: transparent;
  }
  .mc2-report-opts { display: flex; flex-direction: column; gap: 8px; width: 100%; margin-bottom: 14px; }
  .mc2-report-opt {
    padding: 13px 16px; border-radius: 14px; text-align: left; outline: none;
    background: var(--cloud, #F4F7F9); color: var(--ink, #2D364D);
    border: 1.5px solid var(--sand-light, #EBE7DF);
    font-family: var(--font, sans-serif); font-size: 0.88rem; font-weight: 600;
    cursor: pointer; transition: all 0.15s; -webkit-tap-highlight-color: transparent;
  }
  .mc2-report-opt.active {
    border-color: var(--mc2-primary, #5A6993);
    background: rgba(var(--mc2-mesh, 90,105,147), 0.1);
    color: var(--mc2-primary, #5A6993);
  }
  .mc2-report-submit {
    width: 100%; padding: 15px; border-radius: 16px; border: none; outline: none;
    background: #FF3B30; color: #fff;
    font-family: var(--font, sans-serif); font-size: 0.95rem; font-weight: 700;
    cursor: pointer; -webkit-tap-highlight-color: transparent;
  }
  .mc2-report-submit:disabled { opacity: 0.4; cursor: not-allowed; }
`;

// ─── ROOT COMPONENT ───────────────────────────────────────────────────────────

export default function MobileChattr() {
  const currentUser = auth.currentUser;

  const {
    status, queueStatus, matchData, messages,
    partnerTyping, error,
    joinQueue, cancelQueue, broadenQueue,
    sendMessage, sendTyping, sendReaction, leaveRoom, resetError,
  } = useChattrSocket(currentUser);

  const [nickname,         setNickname]         = useState("");
  const [selectedInterest, setSelectedInterest] = useState("");
  const [view,             setView]             = useState("home");
  const [showPromise,      setShowPromise]       = useState(false);
  const [currentPromise,   setCurrentPromise]    = useState("");
  const [elapsed,          setElapsed]          = useState(0);
  const [isEditingCustom,  setIsEditingCustom]  = useState(false);
  const [customTopic,      setCustomTopic]      = useState("");
  const [broadened,        setBroadened]        = useState(false);

  const elapsedRef  = useRef(null);
  /**
   * nicknameRef keeps the current nickname accessible to the broaden
   * effect without putting `nickname` in its dependency array (which
   * would re-create the effect on every keystroke while the user is
   * still on the home screen).
   */
  const nicknameRef = useRef(nickname);
  useEffect(() => { nicknameRef.current = nickname; }, [nickname]);

  // View sync — identical to Chattr.jsx (keyed on queueStatus === "matched")
  useEffect(() => {
    if (queueStatus === "matched" && matchData) {
      if (shouldShowPromise()) {
        setCurrentPromise(getRandomPromise());
        setShowPromise(true);
      } else {
        setView("chat");
      }
    } else if (queueStatus === "queued") {
      setView("queued");
      // Don't reset elapsed here — the broaden effect manages elapsed resets.
      // On fresh queue join, elapsed is already 0 from handleStart.
    } else if (queueStatus === "idle" || queueStatus === "timeout") {
      setView("home");
    }
  }, [queueStatus, matchData]);

  // Queue elapsed timer
  useEffect(() => {
    if (view === "queued") {
      elapsedRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    } else {
      clearInterval(elapsedRef.current);
      // Reset broadened state when leaving the queue (matched, cancelled, timeout)
      setBroadened(false);
    }
    return () => clearInterval(elapsedRef.current);
  }, [view]);

  /**
   * ── AUTO-BROADEN EFFECT ─────────────────────────────────────────────────
   *
   * After BROADEN_AFTER_SECONDS (30s) of searching by specific interest,
   * atomically cancel + re-queue with interest "Any" via broadenQueue().
   *
   * Why this is safe:
   *   1. broadenQueue() sets isBroadeningRef = true in the hook BEFORE
   *      sending any messages, so the message handler is already guarded.
   *   2. cancel_queue + join_queue are sent in the same synchronous tick
   *      over the same WebSocket — server processes them in order.
   *   3. The hook suppresses "queue_cancelled" and any racing "match_timeout"
   *      while isBroadeningRef is true, so queueStatus never touches "idle".
   *   4. The new "queued" confirmation from the server consumes the flag.
   *
   * The elapsed timer resets to 0 so the progress bar restarts for the
   * broadened search phase. broadened=true changes the QueuedView UI.
   *
   * Guard: only fires once — !broadened prevents re-triggering if elapsed
   * passes 30 again after reset.
   */
  useEffect(() => {
    if (view === "queued" && elapsed >= BROADEN_AFTER_SECONDS && !broadened) {
      broadenQueue(nicknameRef.current, "Any");
      setBroadened(true);
      setElapsed(0);
    }
  }, [elapsed, view, broadened, broadenQueue]);

  const handlePromiseAccept = () => {
    markPromiseSeen();
    setShowPromise(false);
    setView("chat");
  };

  const handleStart = () => {
    if (!nickname.trim() || !selectedInterest) return;
    haptic("heavy");
    setElapsed(0);
    setBroadened(false);
    joinQueue(nickname.trim(), selectedInterest);
  };

  const handleLeave = useCallback(() => {
    haptic("heavy");
    leaveRoom();
    setView("home");
  }, [leaveRoom]);

  const handleCancel = () => {
    haptic();
    cancelQueue();
    setBroadened(false);
    setView("home");
  };

  const activeTopic = (view === "chat" && matchData)
    ? (matchData.interest || selectedInterest)
    : selectedInterest;
  const mood = getMood(activeTopic);

  const cssVars = {
    "--mc2-primary":   mood.primary,
    "--mc2-accent":    mood.accent,
    "--mc2-secondary": mood.secondary,
    "--mc2-mesh":      mood.mesh,
  };

  const meshBg = `
    radial-gradient(circle at 40% 40%, rgba(${mood.mesh},0.16) 0%, transparent 55%),
    radial-gradient(circle at 75% 20%, rgba(${mood.mesh},0.10) 0%, transparent 45%)
  `;

  return (
    <div
      className={`mc2-root${view === "chat" ? " mc2-root--chat" : ""}`}
      style={cssVars}
    >
      <style>{BASE_CSS}</style>

      {/* Ambient mesh */}
      <div className="mc2-mesh" style={{ background: meshBg }} />

      {/* Connection status (home/queued only) */}
      {status !== "connected" && view !== "chat" && (
        <div style={{ position: "relative", zIndex: 1, padding: "env(safe-area-inset-top,8px) 16px 0" }}>
          <div className="mc2-status-bar">
            <div className={`mc2-dot mc2-dot--${status}`} />
            <span>{status === "connecting" ? "Connecting…" : "Reconnecting…"}</span>
          </div>
        </div>
      )}

      {view === "home" && (
        <HomeView
          nickname={nickname}
          setNickname={setNickname}
          selectedInterest={selectedInterest}
          setSelectedInterest={setSelectedInterest}
          isEditingCustom={isEditingCustom}
          setIsEditingCustom={setIsEditingCustom}
          customTopic={customTopic}
          setCustomTopic={setCustomTopic}
          onStart={handleStart}
          disabled={status !== "connected"}
          error={error}
          resetError={resetError}
          mood={mood}
          activeTopic={activeTopic}
        />
      )}

      {view === "queued" && (
        <QueuedView
          interest={selectedInterest}
          elapsed={elapsed}
          onCancel={handleCancel}
          mood={mood}
          broadened={broadened}
        />
      )}

      {view === "chat" && matchData && (
        <ChatView
          matchData={matchData}
          messages={messages}
          partnerTyping={partnerTyping}
          onSend={sendMessage}
          onTyping={sendTyping}
          onReact={sendReaction}
          onLeave={handleLeave}
          mood={mood}
          selectedInterest={selectedInterest}
        />
      )}

      {/* Promise modal — once/hour, shown before chat loads */}
      {showPromise && (
        <div className="mc2-overlay">
          <div className="mc2-sheet" onClick={e => e.stopPropagation()}>
            <div className="mc2-sheet-handle" />
            <div className="mc2-sheet-icon">🤝</div>
            <h3 className="mc2-sheet-title">Before you begin</h3>
            <blockquote className="mc2-sheet-quote">"{currentPromise}"</blockquote>
            <p className="mc2-sheet-text">Tap below to enter the conversation with an open heart.</p>
            <div className="mc2-sheet-actions">
              <button
                className="mc2-sheet-primary"
                onClick={() => { haptic("heavy"); handlePromiseAccept(); }}
              >
                I Promise 💛
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── HOME VIEW ────────────────────────────────────────────────────────────────

function HomeView({
  nickname, setNickname, selectedInterest, setSelectedInterest,
  isEditingCustom, setIsEditingCustom, customTopic, setCustomTopic,
  onStart, disabled, error, resetError, mood, activeTopic,
}) {
  const customInputRef = useRef(null);
  const canStart = !disabled && nickname.trim().length >= 1 && selectedInterest;

  useEffect(() => {
    if (isEditingCustom && customInputRef.current) customInputRef.current.focus();
  }, [isEditingCustom]);

  const isCustomActive = selectedInterest && !INTERESTS.find(i => i.value === selectedInterest);

  const saveCustom = () => {
    const val = customTopic.trim();
    if (val) setSelectedInterest(val);
    else if (isCustomActive) setSelectedInterest("");
    setIsEditingCustom(false);
  };

  const handleChipClick = (val) => {
    haptic();
    setSelectedInterest(selectedInterest === val ? "" : val);
  };

  return (
    <div className="mc2-home">
      <h1 className="mc2-home-title">Chattr</h1>
      <p className="mc2-home-sub">
        Talk anonymously with someone who gets it.<br />
        No judgment. Just a real conversation.
      </p>

      {error && (
        <div className="mc2-error">
          <span>{error}</span>
          <button onClick={resetError}>×</button>
        </div>
      )}

      <div className="mc2-field">
        <label className="mc2-label">Your Nickname</label>
        <input
          className="mc2-nick-input"
          value={nickname}
          onChange={e => setNickname(e.target.value)}
          onKeyDown={e => e.key === "Enter" && onStart()}
          placeholder="What should we call you?"
          maxLength={30}
          autoComplete="off"
        />
      </div>

      <div className="mc2-field">
        <label className="mc2-label">What do you want to talk about?</label>
        <div className="mc2-chips">
          {INTERESTS.map(({ value, emoji }) => (
            <button
              key={value}
              className={`mc2-chip${selectedInterest === value ? " active" : ""}`}
              onClick={() => handleChipClick(value)}
            >
              <span>{emoji}</span>
              <span>{value}</span>
            </button>
          ))}

          {/* Custom interest */}
          {isEditingCustom ? (
            <input
              ref={customInputRef}
              className="mc2-custom-input"
              value={customTopic}
              onChange={e => setCustomTopic(e.target.value)}
              onBlur={saveCustom}
              onKeyDown={e => {
                if (e.key === "Enter")  { e.preventDefault(); saveCustom(); }
                if (e.key === "Escape") { setIsEditingCustom(false); setCustomTopic(""); }
              }}
              placeholder="Type a topic…"
              maxLength={30}
            />
          ) : (
            <button
              className={`mc2-chip${isCustomActive ? " active" : ""}`}
              onClick={() => {
                haptic();
                setCustomTopic(isCustomActive ? selectedInterest : "");
                setIsEditingCustom(true);
              }}
            >
              <span>✏️</span>
              <span>{isCustomActive ? selectedInterest : "Other…"}</span>
            </button>
          )}
        </div>
      </div>

      <button
        className="mc2-cta"
        onClick={onStart}
        disabled={!canStart}
      >
        {disabled ? "Connecting…" : "Find Someone to Talk To →"}
      </button>

      {selectedInterest && (
        <div className="mc2-share">
          <span className="mc2-share-text">Know someone who could use this?</span>
          <a
            href={`https://wa.me/?text=${encodeURIComponent(
              `Try Chattr on HealHub — talk anonymously about ${selectedInterest}. https://heal-hub-healing-tfa4.vercel.app/chattr`
            )}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <button className="mc2-share-btn">📤 Share</button>
          </a>
        </div>
      )}
    </div>
  );
}

// ─── QUEUED VIEW ──────────────────────────────────────────────────────────────

function QueuedView({ interest, elapsed, onCancel, broadened }) {
  const meta = INTERESTS.find(i => i.value === interest) || { emoji: "💬", value: interest };

  /**
   * Timer semantics:
   *   Phase 1 (broadened=false): 0→30s, searching by specific interest.
   *     Progress bar fills over 30s. Label says "broadens at 30s".
   *   Phase 2 (broadened=true): elapsed resets to 0, searching all topics.
   *     Progress bar shows a pulsing/indeterminate style (no fixed end).
   *     Label says "searching all topics".
   */
  const timerMax    = broadened ? 60 : BROADEN_AFTER_SECONDS;
  const timerPct    = broadened
    ? Math.min((elapsed / timerMax) * 100, 100)
    : Math.min((elapsed / BROADEN_AFTER_SECONDS) * 100, 100);

  return (
    <div className="mc2-queued">
      <div className="mc2-radar">
        <div className="mc2-radar-ring" />
        <div className="mc2-radar-ring" />
        <div className="mc2-radar-ring" />
        <div className="mc2-radar-icon">{broadened ? "🌐" : meta.emoji}</div>
      </div>

      <h2 className="mc2-queued-title">
        {broadened ? "Expanding search" : "Looking for a match"}
      </h2>

      <p className="mc2-queued-sub">
        {broadened ? (
          <>
            No one available for <strong>{meta.value}</strong> right now
            <br />
            Connecting you with <strong>anyone online</strong>
          </>
        ) : (
          <>Finding someone interested in <strong>{meta.value}</strong></>
        )}
      </p>

      {broadened && (
        <div className="mc2-broadened-badge">
          <span>🔄</span>
          <span>Search broadened automatically</span>
        </div>
      )}

      <div className="mc2-timer-wrap">
        <div className="mc2-timer-track">
          <div
            className="mc2-timer-fill"
            style={{ width: `${timerPct}%` }}
          />
        </div>
        <span className="mc2-timer-text">
          {broadened
            ? `${elapsed}s · searching all topics`
            : `${elapsed}s · broadens at ${BROADEN_AFTER_SECONDS}s`}
        </span>
      </div>

      <button className="mc2-cancel-btn" onClick={onCancel}>Cancel</button>
    </div>
  );
}

// ─── CHAT VIEW ────────────────────────────────────────────────────────────────

function ChatView({
  matchData, messages, partnerTyping,
  onSend, onTyping, onReact, onLeave,
  mood, selectedInterest,
}) {
  const [input,            setInput]           = useState("");
  const [showLeaveConfirm, setShowLeaveConfirm]= useState(false);
  const [showReportModal,  setShowReportModal] = useState(false);
  const [reportReason,     setReportReason]    = useState("");
  const [reportSent,       setReportSent]      = useState(false);
  const [blockedWarning,   setBlockedWarning]  = useState(false);
  const [showMenu,         setShowMenu]        = useState(false);

  const messagesEndRef = useRef(null);
  const textareaRef    = useRef(null);
  const menuRef        = useRef(null);

  // Typing debounce — identical to Chattr.jsx
  const typingSentRef   = useRef(false);
  const typingStopTimer = useRef(null);

  const partnerLeft = matchData?.partnerLeft;
  const interest    = matchData?.interest || selectedInterest;
  const meta        = INTERESTS.find(i => i.value === interest) || { emoji: "💬", value: interest };

  // Suggestions: hide once any real message exists
  const hasChatMessages = messages.some(m => typeof m.isSelf === "boolean");
  const showSuggestions = !hasChatMessages && !partnerLeft;
  const starters        = useMemo(() => getStarters(interest), [interest]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, partnerTyping]);

  useEffect(() => {
    if (!partnerLeft) {
      setTimeout(() => textareaRef.current?.focus(), 350);
    }
  }, [partnerLeft]);

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    const handler = e => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showMenu]);

  // ── Input handlers ──

  const handleInputChange = e => {
    setInput(e.target.value);
    setBlockedWarning(false);

    // Auto-resize textarea
    const el = e.target;
    el.style.height = "44px";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;

    if (!typingSentRef.current) {
      onTyping(true);
      typingSentRef.current = true;
    }
    clearTimeout(typingStopTimer.current);
    typingStopTimer.current = setTimeout(() => {
      onTyping(false);
      typingSentRef.current = false;
    }, 1500);
  };

  const handleSend = () => {
    const text = input.trim();
    if (!text || partnerLeft) return;

    if (containsBadWord(text)) {
      setBlockedWarning(true);
      setTimeout(() => setBlockedWarning(false), 3000);
      return;
    }

    haptic();
    onSend(text);
    setInput("");

    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = "44px";
        textareaRef.current.focus();
      }
    });

    clearTimeout(typingStopTimer.current);
    onTyping(false);
    typingSentRef.current = false;
    setTimeout(scrollToBottom, 50);
  };

  const handleKeyDown = e => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleSuggestionTap = text => {
    haptic();
    setInput(text);
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.style.height = "44px";
        textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
      }
    });
  };

  const handleReport = () => {
    if (!reportReason) return;
    console.log("Report:", { reportedNickname: matchData.partnerNickname, reason: reportReason });
    setReportSent(true);
    setTimeout(() => {
      setShowReportModal(false);
      setReportSent(false);
      setReportReason("");
    }, 2000);
  };

  return (
    <div className="mc2-chat">

      {/* Grid row 1: header */}
      <div className="mc2-chat-hdr">
        <div className="mc2-hdr-left">
          <div className="mc2-hdr-avatar">{meta.emoji}</div>
          <div>
            <div className="mc2-hdr-name">{matchData.partnerNickname}</div>
            <div className="mc2-hdr-status">
              <div className="mc2-online-dot" />
              {meta.value} · Anonymous
            </div>
          </div>
        </div>
        <div className="mc2-hdr-right">
          <div className="mc2-menu-wrap" ref={menuRef}>
            <button
              className="mc2-menu-btn"
              onClick={() => setShowMenu(s => !s)}
              aria-label="Options"
            >
              ⋮
            </button>
            {showMenu && (
              <div className="mc2-dropdown">
                <button
                  className="mc2-dd-item mc2-dd-item--danger"
                  onClick={() => { setShowMenu(false); setShowReportModal(true); }}
                >
                  Report user
                </button>
                <button
                  className="mc2-dd-item"
                  onClick={() => { setShowMenu(false); setShowLeaveConfirm(true); }}
                >
                  Leave chat
                </button>
              </div>
            )}
          </div>
          <button
            className="mc2-leave-btn"
            onClick={() => { haptic(); setShowLeaveConfirm(true); }}
          >
            Leave
          </button>
        </div>
      </div>

      {/* Grid row 2: messages */}
      <div className="mc2-msgs">
        <div className="mc2-system-msg">
          <span>💙 Connected with <strong>{matchData.partnerNickname}</strong></span>
          <span className="mc2-system-sub">Safe space · be kind</span>
        </div>

        {messages.map(msg => (
          <MessageBubble
            key={msg.id}
            msg={msg}
            partnerNickname={matchData.partnerNickname}
            onReact={onReact}
          />
        ))}

        {partnerTyping && !partnerLeft && (
          <div className="mc2-row mc2-row--other">
            <div className="mc2-avatar-sm">
              {matchData.partnerNickname?.[0]?.toUpperCase() || "?"}
            </div>
            <div>
              <div className="mc2-typing-bubble">
                <div className="mc2-td" />
                <div className="mc2-td" />
                <div className="mc2-td" />
              </div>
            </div>
          </div>
        )}

        {partnerLeft && (
          <div className="mc2-system-msg mc2-system-msg--end">
            <span>
              {matchData.partnerLeftReason === "disconnected" ? "📡" : "👋"}{" "}
              <strong>{matchData.partnerNickname}</strong>{" "}
              {matchData.partnerLeftReason === "disconnected"
                ? "lost connection"
                : "left the chat"}
            </span>
            <span className="mc2-system-sub">
              {matchData.partnerLeftReason === "disconnected"
                ? "They may reconnect shortly."
                : "Hope the conversation helped. Take care."}
            </span>
            <button
              className="mc2-sheet-primary"
              style={{ marginTop: 12, width: "auto", padding: "11px 22px", fontSize: "0.9rem" }}
              onClick={onLeave}
            >
              Find Someone New
            </button>
          </div>
        )}

        <div ref={messagesEndRef} style={{ height: 1 }} />
      </div>

      {/* Grid row 3: input bar */}
      {!partnerLeft && (
        <div className="mc2-bottom">
          {showSuggestions && (
            <div className="mc2-suggestions">
              {starters.map((text, i) => (
                <button
                  key={i}
                  className="mc2-suggestion"
                  onClick={() => handleSuggestionTap(text)}
                >
                  {text}
                </button>
              ))}
            </div>
          )}

          {blockedWarning && (
            <div className="mc2-blocked">
              ⚠️ Please keep this a safe space — that language isn't allowed here.
            </div>
          )}

          <div className="mc2-input-bar">
            <textarea
              ref={textareaRef}
              className={`mc2-textarea${blockedWarning ? " mc2-textarea--blocked" : ""}`}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Write something kind…"
              rows={1}
              enterKeyHint="send"
              autoCapitalize="sentences"
            />
            <button
              className="mc2-send"
              onClick={handleSend}
              disabled={!input.trim()}
              aria-label="Send"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Leave confirm sheet */}
      {showLeaveConfirm && (
        <div className="mc2-overlay" onClick={() => setShowLeaveConfirm(false)}>
          <div className="mc2-sheet" onClick={e => e.stopPropagation()}>
            <div className="mc2-sheet-handle" />
            <div className="mc2-sheet-icon">👋</div>
            <h3 className="mc2-sheet-title">Leave this chat?</h3>
            <p className="mc2-sheet-text">
              {matchData.partnerNickname} will be notified that you've left.
            </p>
            <div className="mc2-sheet-actions">
              <button
                className="mc2-sheet-danger"
                onClick={() => { setShowLeaveConfirm(false); onLeave(); }}
              >
                Yes, Leave
              </button>
              <button
                className="mc2-sheet-secondary"
                onClick={() => setShowLeaveConfirm(false)}
              >
                Stay in Chat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report sheet */}
      {showReportModal && (
        <div
          className="mc2-overlay"
          onClick={() => { setShowReportModal(false); setReportReason(""); setReportSent(false); }}
        >
          <div className="mc2-sheet" onClick={e => e.stopPropagation()}>
            <div className="mc2-sheet-handle" />
            {reportSent ? (
              <>
                <div className="mc2-sheet-icon">✅</div>
                <h3 className="mc2-sheet-title">Report submitted</h3>
                <p className="mc2-sheet-text">
                  Thank you for keeping HealHub safe. We'll review this shortly.
                </p>
              </>
            ) : (
              <>
                <h3 className="mc2-sheet-title" style={{ marginTop: 0 }}>
                  Report {matchData.partnerNickname}
                </h3>
                <p className="mc2-sheet-text">What's happening?</p>
                <div className="mc2-report-opts">
                  {REPORT_REASONS.map(reason => (
                    <button
                      key={reason}
                      className={`mc2-report-opt${reportReason === reason ? " active" : ""}`}
                      onClick={() => setReportReason(reason)}
                    >
                      {reason}
                    </button>
                  ))}
                </div>
                <button
                  className="mc2-report-submit"
                  onClick={handleReport}
                  disabled={!reportReason}
                >
                  Submit Report
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MESSAGE BUBBLE ───────────────────────────────────────────────────────────

function MessageBubble({ msg, partnerNickname, onReact }) {
  const [showReactions, setShowReactions] = useState(false);
  const hoverTimer = useRef(null);
  const touchTimer = useRef(null);

  const isSelf        = msg.isSelf;
  const myReaction    = msg.myReaction    || null;
  const theirReaction = msg.theirReaction || null;

  const handleReactionClick = emoji => {
    haptic();
    const next = myReaction === emoji ? null : emoji;
    onReact?.(msg.id, next);
    setShowReactions(false);
  };

  // Long-press for mobile
  const handleTouchStart = () => {
    touchTimer.current = setTimeout(() => {
      haptic("heavy");
      setShowReactions(true);
    }, 500);
  };
  const handleTouchEnd = () => clearTimeout(touchTimer.current);

  // Hover for desktop / webview
  const handleMouseEnter = () => {
    hoverTimer.current = setTimeout(() => setShowReactions(true), 400);
  };
  const handleMouseLeave = () => {
    clearTimeout(hoverTimer.current);
    setShowReactions(false);
  };

  const timeStr = msg.timestamp
    ? new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "";

  return (
    <div
      className={`mc2-row mc2-row--${isSelf ? "self" : "other"}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchEnd}
    >
      {!isSelf && (
        <div className="mc2-avatar-sm">
          {msg.sender?.[0]?.toUpperCase() || "?"}
        </div>
      )}

      <div className="mc2-bubble-group">
        {!isSelf && <div className="mc2-bubble-sender">{msg.sender}</div>}

        <div className="mc2-bubble-with-reaction">
          <div className={`mc2-bubble mc2-bubble--${isSelf ? "self" : "other"}`}>
            {msg.text}
          </div>

          {showReactions && (
            <div className={`mc2-reaction-picker${isSelf ? " mc2-reaction-picker--self" : ""}`}>
              {REACTION_EMOJIS.map(emoji => (
                <button
                  key={emoji}
                  className={`mc2-rxn-btn${myReaction === emoji ? " mc2-rxn-btn--active" : ""}`}
                  onClick={() => handleReactionClick(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>

        {(myReaction || theirReaction) && (
          <div className={`mc2-reaction-display${isSelf ? " mc2-reaction-display--self" : ""}`}>
            {myReaction && (
              <span
                className="mc2-rxn-emoji mc2-rxn-emoji--mine"
                title="Your reaction"
                onClick={() => handleReactionClick(myReaction)}
              >
                {myReaction}
              </span>
            )}
            {theirReaction && theirReaction !== myReaction && (
              <span className="mc2-rxn-emoji" title={`${partnerNickname}'s reaction`}>
                {theirReaction}
              </span>
            )}
            {theirReaction && theirReaction === myReaction && (
              <span className="mc2-rxn-emoji mc2-rxn-emoji--both" title="Both reacted">
                {myReaction} ×2
              </span>
            )}
          </div>
        )}

        {timeStr && (
          <div className={`mc2-bubble-time${isSelf ? " mc2-bubble-time--self" : ""}`}>
            {timeStr}
          </div>
        )}
      </div>
    </div>
  );
}